import React, { PropsWithChildren, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmationDialog } from "./ConfirmationDialog";
import { ConfirmationDialogProps } from "./ConfirmationDialog.types";
import { ConfirmationDialogContext } from "./ConfirmationDialogContext";
import { ConfirmationDialogConfig, ConfirmationDialogContextValue } from "./ConfirmationDialogContextProvider.types";

export const ConfirmationDialogContextProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
  const [confirmationDialogProps, setConfirmationDialogProps] = useState<ConfirmationDialogProps | null>(null);
  const { t } = useTranslation();

  const blurActiveElement = useCallback(() => {
    (document.activeElement as HTMLElement | undefined)?.blur();
  }, []);

  const showConfirmationDialog = useCallback(
    (config: ConfirmationDialogConfig) => {
      blurActiveElement();
      setConfirmationDialogProps({
        ...config,
        open: true,
      });
    },
    [blurActiveElement]
  );

  const showOkDialog = useCallback(
    (title: string, text: string, onClose: (confirm: boolean) => void) => {
      blurActiveElement();
      setConfirmationDialogProps({
        open: true,
        dialogTitleText: title,
        dialogText: text,
        showDiscardButton: false,
        onClose,
      });
    },
    [blurActiveElement]
  );

  const showYesNoDialog = useCallback(
    (title: string, text: string, onClose: (confirm: boolean) => void) => {
      blurActiveElement();
      setConfirmationDialogProps({
        open: true,
        dialogTitleText: title,
        dialogText: text,
        agreeText: t("common.yes"),
        showDiscardButton: true,
        discardText: t("common.no"),
        onClose,
      });
    },
    [blurActiveElement, t]
  );

  const closeConfirmationDialog = useCallback(
    (confirm: boolean) => {
      // The dialog stays mounted and clickable while it fades out; only the first choice counts,
      // otherwise a double-click (or cancel followed by confirm) runs the caller's action again.
      if (confirmationDialogProps?.open !== true) {
        return;
      }

      confirmationDialogProps.onClose(confirm);
      setConfirmationDialogProps({
        ...confirmationDialogProps,
        open: false,
      });
    },
    [confirmationDialogProps]
  );

  const contextValue: ConfirmationDialogContextValue = {
    showConfirmationDialog,
    showOkDialog,
    showYesNoDialog,
  };

  return (
    <>
      <ConfirmationDialogContext.Provider value={contextValue}>{props.children}</ConfirmationDialogContext.Provider>
      {confirmationDialogProps != null && (
        <ConfirmationDialog
          open={confirmationDialogProps.open}
          onClose={closeConfirmationDialog}
          dialogTitleText={confirmationDialogProps.dialogTitleText}
          dialogText={confirmationDialogProps.dialogText}
          agreeText={confirmationDialogProps.agreeText}
          agreeColor={confirmationDialogProps.agreeColor}
          agreeIcon={confirmationDialogProps.agreeIcon}
          discardText={confirmationDialogProps.discardText}
          showDiscardButton={confirmationDialogProps.showDiscardButton}
        />
      )}
    </>
  );
};
